'use client'

import { useMemo } from 'react'

import { IS_TMS_DELIVERY } from 'shared/config'
import { isNilOrEmpty, isNotNilOrEmpty } from 'shared/utils/ramda'

import { Cart, CartItem, CartPagePricingState } from '../../../types'
import { CartValidationErrors } from '../utils'

interface CartValidationProps {
  cart: Cart | null
  cartState: CartPagePricingState | null
  pageType: string
  productsNotAvailableInStock?: CartItem[]
  productsQuantityExceeded?: boolean
  productsNotAvailableForDeliveryMethod?: CartItem[]
}

const useCartValidation = ({
  cart,
  cartState,
  pageType,
  productsNotAvailableInStock,
  productsQuantityExceeded,
  productsNotAvailableForDeliveryMethod
}: CartValidationProps) => {
  return useMemo(() => {
    const errors: string[] = []

    const discountCode = isNotNilOrEmpty(cart?.discountCodes)
      ? cart?.discountCodes[0]
      : null

    if (discountCode) {
      const cartItemsValue = cart?.itemsValue?.gross?.value || 0
      const discountCodeValue = discountCode?.minimumOrderValue?.value || 0
      const discountCodeValueFormatted = String(
        discountCode?.minimumOrderValue?.formatted
      )

      if (cartItemsValue < discountCodeValue) {
        errors.push(
          `Minimalna wartość koszyka dla podanego kodu rabatowego to ${discountCodeValueFormatted?.replace(
            '&nbsp;',
            ''
          )}`
        )
      }
    }

    if (
      isNotNilOrEmpty(cartState?.minOrderValue) &&
      cartState?.minOrderValueMissing &&
      cartState?.minOrderValueMissing > 0
    ) {
      errors.push(CartValidationErrors.MIN_ORDER_VALUE_MISSING)
    }

    if (!cartState?.validItemsForDiscountCode) {
      errors.push('W koszyku jest zbyt mało produktów które możemy rabatować')
    }

    if (pageType === 'cart') {
      if (isNilOrEmpty(cart?.items)) errors.push('Brak produktów w koszyku')
      if (isNotNilOrEmpty(productsNotAvailableInStock))
        errors.push('Pewne produkty nie są dostępne, zamień je lub usuń')
      if (productsQuantityExceeded)
        errors.push(
          'Jeden lub więcej produktów przekracza limit dostępności w magazynie'
        )
    }

    if (pageType === 'delivery') {
      // comment it for now to avoid blocking the checkout because if we have deliveryAddressId it means that address is supported
      // if (!isPostalCodeAndCitySupported)
      //   errors.push('Na chwilę obecną nie dowozimy pod wskazany adres')
      if (cart?.deliverySlot?.hasUnavailableCartItems)
        errors.push('Pewne produkty nie są dostępne w wybranym slocie dostaw')
      if (isNilOrEmpty(IS_TMS_DELIVERY && cart?.tmsReservationSlotData))
        errors.push('Nie zarezerwowano terminu dostawy')
      if (isNilOrEmpty(!IS_TMS_DELIVERY && cart?.deliverySlotId))
        errors.push('Nie wybrano terminu dostawy')
      if (isNilOrEmpty(cart?.deliveryAddressId))
        errors.push('Nie wybrano adresu dostawy')
      if (isNotNilOrEmpty(productsNotAvailableForDeliveryMethod))
        errors.push(
          'Pewne produkty nie są dostępne w wybranej metodzie dostawy'
        )
    }

    return errors
  }, [
    cart,
    pageType,
    // isPostalCodeAndCitySupported,
    productsNotAvailableInStock,
    cartState,
    productsQuantityExceeded,
    productsNotAvailableForDeliveryMethod
  ])
}

export default useCartValidation
